// <scroll-carousel>: a native scroll-snap track with previous/next buttons and a custom scroll indicator.
// Markup: [data-carousel-track] (the scroller), optional [data-carousel-prev] / [data-carousel-next] buttons, optional
// [data-carousel-scrollbar] > [data-carousel-thumb]. Buttons follow the reading direction ("next" moves towards the end
// of the row in RTL as in LTR). The indicator is a thin track with a brand thumb sized visible/total (min 60px), placed
// from the inline start (the right edge in Arabic), draggable with mouse/touch, and the track jumps on click. It is
// decorative for assistive tech (the track itself is keyboard-scrollable) and hidden when nothing overflows (it keeps its
// space, so showing it never shifts the page). A press within the thumb's span grabs it; elsewhere it jumps there. Touch:
// the bar lets vertical swipes scroll the page (touch-action: pan-y) — a drag starts only after a sideways move, a tap jumps.
// Without JS the track still scrolls by touch, trackpad and keyboard.

export const MIN_THUMB = 60;

// Pixels to scroll for one step: 80% of the visible width, signed for the reading direction.
export function stepDistance(clientWidth, direction, rtl) {
  return Math.round(clientWidth * 0.8) * direction * (rtl ? -1 : 1);
}

// Position along the row as 0..1 whatever the browser's RTL scrollLeft convention (0 → negative or 0 → positive).
export function scrollProgress(scrollLeft, scrollWidth, clientWidth) {
  const max = scrollWidth - clientWidth;
  if (max <= 1) return { start: true, end: true, ratio: 0, visible: 1 };
  const ratio = Math.min(Math.abs(scrollLeft) / max, 1);
  return { start: ratio <= 0.01, end: ratio >= 0.99, ratio, visible: clientWidth / scrollWidth };
}

// Thumb size and offset (from the inline start) in pixels for a track of trackWidth.
export function thumbGeometry(trackWidth, visible, ratio, min = MIN_THUMB) {
  const size = Math.min(trackWidth, Math.max(min, Math.round(visible * trackWidth)));
  return { size, offset: Math.round(ratio * (trackWidth - size)) };
}

// True when a pointer at `position` px from the inline start is on the thumb (its span plus `slop` px either side).
export function onThumb(position, offset, size, slop = 6) {
  return position >= offset - slop && position <= offset + size + slop;
}

// Scroll ratio for a pointer at `position` px from the inline start, centring the thumb on it.
export function ratioAt(position, trackWidth, thumbSize) {
  const room = trackWidth - thumbSize;
  if (room <= 0) return 0;
  return Math.min(1, Math.max(0, (position - thumbSize / 2) / room));
}

const clamp = (value) => Math.min(1, Math.max(0, value));

export class ScrollCarousel extends (globalThis.HTMLElement ?? class {}) {
  connectedCallback() {
    this.track = this.querySelector('[data-carousel-track]');
    if (!this.track || this.controller) return;
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.prev = this.querySelector('[data-carousel-prev]');
    this.next = this.querySelector('[data-carousel-next]');
    this.bar = this.querySelector('[data-carousel-scrollbar]');
    this.thumb = this.bar?.querySelector('[data-carousel-thumb]');
    this.prev?.addEventListener('click', () => this.go(-1), { signal });
    this.next?.addEventListener('click', () => this.go(1), { signal });
    this.track.addEventListener('scroll', () => this.update(), { passive: true, signal });
    this.resizer = new ResizeObserver(() => this.update());
    this.resizer.observe(this.track);
    // Cards rendered later (recently viewed, recommendations) change the scroll width without resizing the track.
    this.mutations = new MutationObserver(() => this.update());
    this.mutations.observe(this.track, { childList: true });
    if (this.bar && this.thumb) this.bindScrollbar(signal);
    // Theme editor: selecting a block scrolls it into view.
    document.addEventListener('shopify:block:select', (event) => {
      if (this.contains(event.target)) event.target.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }, { signal });
    this.update();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
    this.resizer?.disconnect();
    this.mutations?.disconnect();
  }

  get rtl() {
    return getComputedStyle(this.track).direction === 'rtl';
  }

  get smooth() {
    return matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }

  go(direction) {
    this.track.scrollBy({ left: stepDistance(this.track.clientWidth, direction, this.rtl), behavior: this.smooth });
  }

  // Scroll so the row sits at `ratio` (0 = start of the row, 1 = end).
  scrollToRatio(ratio, behavior = 'auto') {
    const max = this.track.scrollWidth - this.track.clientWidth;
    this.track.scrollTo({ left: clamp(ratio) * max * (this.rtl ? -1 : 1), behavior });
  }

  // Pointer position from the inline start of the indicator (the right edge in RTL).
  inlinePosition(event) {
    const box = this.bar.getBoundingClientRect();
    return this.rtl ? box.right - event.clientX : event.clientX - box.left;
  }

  bindScrollbar(signal) {
    let drag = null;
    // Start dragging: a press off the thumb first jumps there, then the drag continues from that point.
    const activate = (event) => {
      if (!drag.grab) {
        drag.startRatio = ratioAt(drag.start, drag.trackWidth, drag.size);
        this.scrollToRatio(drag.startRatio);
      }
      drag.active = true;
      this.bar.setPointerCapture(event.pointerId);
      this.bar.classList.add('is-dragging');
      this.track.classList.add('is-dragging');
    };
    this.bar.addEventListener('pointerdown', (event) => {
      if (event.button !== 0) return;
      const trackWidth = this.bar.clientWidth;
      const state = scrollProgress(this.track.scrollLeft, this.track.scrollWidth, this.track.clientWidth);
      const { size, offset } = thumbGeometry(trackWidth, state.visible, state.ratio);
      const start = this.inlinePosition(event);
      drag = { pointer: event.pointerId, start, startRatio: state.ratio, trackWidth, size, room: Math.max(1, trackWidth - size),
        grab: onThumb(start, offset, size), touch: event.pointerType !== 'mouse', active: false };
      if (drag.touch) return; // wait: a vertical swipe belongs to the page
      event.preventDefault();
      activate(event);
    }, { signal });
    this.bar.addEventListener('pointermove', (event) => {
      if (!drag || event.pointerId !== drag.pointer) return;
      const delta = this.inlinePosition(event) - drag.start;
      if (!drag.active) {
        if (Math.abs(delta) < 6) return;
        activate(event);
      }
      this.scrollToRatio(drag.startRatio + delta / drag.room);
    }, { signal });
    const end = (event) => {
      if (!drag || event.pointerId !== drag.pointer) return;
      // A tap on the track (touch, no movement) jumps there.
      if (event.type === 'pointerup' && !drag.active && !drag.grab) this.scrollToRatio(ratioAt(drag.start, drag.trackWidth, drag.size), this.smooth);
      drag = null;
      this.bar.classList.remove('is-dragging');
      // Restoring scroll-snap lets the row settle on the nearest card.
      this.track.classList.remove('is-dragging');
    };
    this.bar.addEventListener('pointerup', end, { signal });
    this.bar.addEventListener('pointercancel', end, { signal });
  }

  update() {
    const state = scrollProgress(this.track.scrollLeft, this.track.scrollWidth, this.track.clientWidth);
    const scrollable = !(state.start && state.end);
    if (this.prev) this.prev.disabled = state.start;
    if (this.next) this.next.disabled = state.end;
    this.toggleAttribute('data-scrollable', scrollable);
    if (!this.bar || !this.thumb) return;
    this.bar.hidden = !scrollable;
    if (!scrollable) return;
    const { size, offset } = thumbGeometry(this.bar.clientWidth, state.visible, state.ratio);
    this.thumb.style.inlineSize = `${size}px`;
    this.thumb.style.insetInlineStart = `${offset}px`;
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('scroll-carousel')) customElements.define('scroll-carousel', ScrollCarousel);
